import { useRef, useState, useEffect } from "react"
import { Card, Button, Alert } from "react-bootstrap"
import { Camera, X, RefreshCw } from "lucide-react"
import { BarcodeDetector } from "barcode-detector"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

type QrScannerProps = {
  onScan: (hash: string, eventId: string) => void
}

const QrScanner = ({ onScan }: QrScannerProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [error, setError] = useState<string>("")
  const [isScanning, setIsScanning] = useState(false)
  const [lastScanned, setLastScanned] = useState<string>("")
  const [supportsBarcode, setSupportsBarcode] = useState(false)
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment") // Default to back camera
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const scanIntervalRef = useRef<number | null>(null)

  // Check if Barcode Detection API is supported
  useEffect(() => {
    if ("BarcodeDetector" in window) {
      setSupportsBarcode(true)
      console.log("Barcode Detection API is supported")
    } else {
      console.log("Barcode Detection API not supported")
    }
  }, [])

  const extractHashAndEventId = (qrCodeValue: string): { hash: string; eventId: string } | null => {
    console.log("QR Code raw value:", qrCodeValue)
    try {
      const url = new URL(qrCodeValue)
      const pathSegments = url.pathname.split("/").filter(Boolean)

      // Expected pattern: /api/check-qrcode/{hash}/{event_id}
      // Example: https://api.mazoom.sa/api/check-qrcode/6fdaac88720cad5ec437eb5db03649e8/58530
      // pathSegments: ["api", "check-qrcode", "hash", "event_id"]
      
      if (pathSegments.length >= 4 && pathSegments[0] === "api" && pathSegments[1] === "check-qrcode") {
        const hash = pathSegments[2]
        const eventId = pathSegments[3]
        console.log("✅ Extracted from check-qrcode pattern - Hash:", hash, "Event ID:", eventId)
        return { hash, eventId }
      }
      
      console.error("❌ Could not extract hash and event_id from URL path segments:", pathSegments)
      return null
    } catch {
      // Not a URL, log error
      console.log("❌ Not a valid URL:", qrCodeValue)
      return null
    }
  }

  const scanWithBarcodeDetector = async () => {
    if (!videoRef.current || !("BarcodeDetector" in window)) return

    try {
      const barcodeDetector = new BarcodeDetector({ formats: ["qr_code"] })
      const barcodes = await barcodeDetector.detect(videoRef.current)

      if (barcodes.length > 0) {
        const qrCode = barcodes[0]
        console.log("QR Code detected:", qrCode.rawValue)

        const result = extractHashAndEventId(qrCode.rawValue)
        if (result) {
          console.log("Successfully extracted hash and event ID:", result)
          setLastScanned(result.hash.substring(0, 16) + "...")
          onScan(result.hash, result.eventId)

          // Stop scanning after successful scan
          stopScanning()
        } else {
          // Invalid QR code format - stop scanning and navigate to error page
          console.error("❌ Invalid QR code format - navigating to error page")
          stopScanning()
          navigate("/scanner/invalid")
        }
      }
    } catch (err) {
      console.error("Error scanning with BarcodeDetector:", err)
    }
  }

  const startScanning = async () => {
    try {
      setError("")
      setIsScanning(true)

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      })

      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()

        if (supportsBarcode) {
          scanIntervalRef.current = window.setInterval(scanWithBarcodeDetector, 100)
        } else {
          setError(t("scanner.errors.browserNotSupported"))
        }
      }
    } catch (err) {
      console.error("Error starting camera:", err)
      setError(t("scanner.errors.cameraPermission"))
      setIsScanning(false)
    }
  }

  const stopScanning = () => {
    console.log("Stopping scanner...")

    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null
    }

    setIsScanning(false)
  }

  const flipCamera = async () => {
    console.log("Flipping camera...")
    
    // Stop current stream
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    // Toggle facing mode
    const newFacingMode = facingMode === "environment" ? "user" : "environment"
    setFacingMode(newFacingMode)

    // Start camera with new facing mode
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      })

      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()

        if (supportsBarcode) {
          scanIntervalRef.current = window.setInterval(scanWithBarcodeDetector, 100)
        }
      }
    } catch (err) {
      console.error("Error flipping camera:", err)
      setError(t("scanner.errors.cameraPermission"))
      setIsScanning(false)
    }
  }

  useEffect(() => {
    return () => {
      stopScanning()
    }
  }, [])

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="text-center mb-3">
          <h5 className="mb-2">{t("scanner.scanQrCode")}</h5>
          <p className="text-muted small">
            {isScanning ? t("scanner.positionQrCode") : t("scanner.useCamera")}
          </p>
          {lastScanned && (
            <p className="text-muted small font-monospace">
              {t("scanner.lastScanned")}: {lastScanned}
            </p>
          )}
        </div>

        <div
          className="mx-auto rounded overflow-hidden bg-dark position-relative"
          style={{ maxWidth: "500px", minHeight: "300px" }}
        >
          {isScanning ? (
            <>
              <video
                ref={videoRef}
                className="w-100 h-100"
                style={{ objectFit: "cover", minHeight: "300px" }}
                playsInline
                muted
              />
              <div
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{ pointerEvents: "none" }}
              >
                <div
                  className="position-absolute border border-primary border-3 rounded"
                  style={{
                    top: "15%",
                    left: "15%",
                    right: "15%",
                    bottom: "15%",
                    borderStyle: "dashed",
                  }}
                />
              </div>
              
              {/* Flip Camera Button */}
              <Button
                variant="light"
                onClick={flipCamera}
                className="position-absolute bottom-0 end-0 m-3 rounded-circle d-flex align-items-center justify-content-center p-0"
                style={{ 
                  width: "50px", 
                  height: "50px",
                  minWidth: "50px",
                  minHeight: "50px",
                  maxWidth: "50px",
                  maxHeight: "50px",
                  zIndex: 10,
                  pointerEvents: "auto",
                  backgroundColor: "white"
                }}
                title="Flip Camera"
              >
                <RefreshCw size={20} />
              </Button>
            </>
          ) : (
            <div 
              className="d-flex align-items-center justify-content-center" 
              style={{ 
                width: "100%",
                height: "300px",
                minHeight: "300px"
              }}
            >
              <div className="text-center">
                <Camera size={64} className="text-secondary mb-3" />
                <p className="text-muted mb-0">{t("scanner.readyToScan")}</p>
              </div>
            </div>
          )}
        </div>

        {error && (
          <Alert variant="danger" className="mt-3 mb-0" dismissible onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <div className="d-flex justify-content-center gap-2 mt-3">
          {!isScanning ? (
            <Button variant="dark" size="lg" onClick={startScanning} className="d-flex align-items-center justify-content-center">
              <Camera size={18} className="me-2" />
              {t("scanner.startScanning")}
            </Button>
          ) : (
            <Button variant="danger" size="lg" onClick={stopScanning} className="d-flex align-items-center justify-content-center">
              <X size={18} className="me-2" />
              {t("scanner.stopScanning")}
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}

export default QrScanner

